import io from "socket.io-client";
import {
  updateRoomState,
  updateRoundState,
  updateEntriesState,
  updateMinigamesState,
  updateUserState} from "./redux/Actions";
import store from "./redux/Store";
import Config from "./constant/Config";
import { getEntries } from "./services/EntryService";
import { getMinigames } from "./services/MinigameService";
import { getRoom } from "./services/RoomService";
import { getRound } from "./services/RoundService";
import { getUser } from "./services/UserService";

window.io = io;
window.store = store;
window.Config = Config;

const initRoom = async (roomId) => {
  const roomState = await getRoom(roomId);
  if (roomState) {
    store.dispatch(updateRoomState(roomState));
    return roomState;
  }
};

const initRound = async (currentRoundId) => {
  let inRound = false;
  if (currentRoundId) {
    const roundState = await getRound(currentRoundId);
    if (roundState) {
      inRound = true;
      store.dispatch(updateRoundState(roundState));
    }
  }

  if (!inRound) {
    store.dispatch(updateRoundState(null));
  }
};

const initEntries = async (roomId) => {
  const entriesState = await getEntries(roomId);
  if (entriesState) {
    store.dispatch(updateEntriesState(entriesState));
  }
};

const initMinigames = async () => {
  const minigamesState = await getMinigames();
  if (minigamesState) {
    store.dispatch(updateMinigamesState(minigamesState));
  }
};

const initUser = async (roomId) => {
  const userIdByRoomIdJson = localStorage.getItem("userIdByRoomId");
  let loggedIn = false;
  if (userIdByRoomIdJson !== null) {
    const userIdByRoomId = JSON.parse(userIdByRoomIdJson);
    const userId = userIdByRoomId[roomId];
    if (userId) {
      loggedIn = true;
      const userState = await getUser(userId);
      if (userState) {
        store.dispatch(updateUserState(userState));
      }
    }
  }

  if (!loggedIn) {
    store.dispatch(updateUserState( { "userId": null }));
  }
};

let connectedRoomId = null;

const socket = window.io(Config.apiurl);
export const connectToRoom = async (roomId) => {
  if (roomId === null) {
    throw new Error("RoomId is null");
  }
  // Clean up
  if (connectedRoomId === roomId) {
    console.log(`Already connected to room: ${roomId}`);
    return;
  } else if (connectedRoomId !== null) {
    console.log(`Disconnecting from old room: ${connectedRoomId}`);
    socket.emit("leave", connectedRoomId);
  }
  connectedRoomId = roomId;
  console.log(`Connecting to room: ${roomId}`);

  socket.removeAllListeners("connect");
  socket.removeAllListeners("room");
  socket.removeAllListeners("round");
  socket.removeAllListeners("entries");
  socket.removeAllListeners("user");

  // init - all independent calls fire in parallel; only initRound waits on room.
  const roomPromise = initRoom(roomId);
  const [room] = await Promise.all([
    roomPromise,
    roomPromise.then((r) => initRound(r?.currentRoundId)),
    initEntries(roomId),
    initMinigames(),
    initUser(roomId),
  ]);

  socket.emit("join", roomId);
  socket.io.on('reconnect', () => {
    socket.emit("join", roomId);
  });

  socket.on("room", (roomState) => {
    if (room.roomId === roomState.roomId) {
      store.dispatch(updateRoomState({...roomState}));
    } else {
      console.warn("Ignoring room update for old room. " + 
        `[RoomId: ${roomState.roomId}, CurrentRoomId: ${room.roomId}]`);
    }
  });
  socket.on("round", (roundState) => {
    store.dispatch(updateRoundState({...roundState}));
  });
  socket.on("entries", (entriesState) => {
    store.dispatch(updateEntriesState([...entriesState]));
  });
  socket.on("user", (userState) => {
    const userIdByRoomIdJson = localStorage.getItem("userIdByRoomId");
    if (userIdByRoomIdJson !== null) {
      const userIdByRoomId = JSON.parse(userIdByRoomIdJson);
      const userId = userIdByRoomId[roomId];
      if (userId === userState.userId) {
        store.dispatch(updateUserState({...userState}));
      }
    }
  });
};

export const disconnectFromRoom = () => {
  if (connectedRoomId !== null) {
    console.log(`Disconnecting from room: ${connectedRoomId}`);
    socket.emit("leave", connectedRoomId);
    connectedRoomId = null;
  }
}
